import { useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';

/**
 * Hook to manage evidence documents (uploads, camera captures, and deletions).
 */
export const useEvidence = (currentOrg, user, onSuccess) => {
    const [evidenceUploading, setEvidenceUploading] = useState(false);
    const [deletingEvidence, setDeletingEvidence] = useState(false);

    const uploadCapturedImage = async (emissionId, capturedImage) => {
        if (!capturedImage || !currentOrg) return;

        setEvidenceUploading(true);
        try {
            const filePath = `${currentOrg.id}/${emissionId}/evidence_${Date.now()}.jpg`;

            const { error: uploadError } = await supabase.storage
                .from('evidence-vault')
                .upload(filePath, capturedImage);

            if (uploadError) throw uploadError;
            await supabase.from('documents').insert({
                id: crypto.randomUUID(),
                organisation_id: currentOrg.id,
                user_id: user?.id,
                emission_entry_id: emissionId,
                filename: `photo_${Date.now()}.jpg`,
                file_type: 'image/jpeg',
                file_size: capturedImage.size,
                storage_path: filePath,
                document_type: 'evidence'
            });

            // Get emission details for history record
            const { data: emissionData } = await supabase
                .from('emissions_data')
                .select('category, quantity, unit, co2_equivalent, entry_date')
                .eq('id', emissionId)
                .single();

            // Create entry history record for evidence attachment
            if (emissionData) {
                await supabase.from('entry_history').insert({
                    id: crypto.randomUUID(),
                    emission_entry_id: emissionId,
                    organisation_id: currentOrg.id,
                    user_id: user?.id,
                    action: 'evidence_added',
                    category: emissionData.category,
                    quantity: emissionData.quantity,
                    unit: emissionData.unit,
                    co2_equivalent: emissionData.co2_equivalent,
                    entry_date: emissionData.entry_date,
                    notes: `Captured and attached photo evidence`,
                    user_email: user?.email
                });
            }

            // Get public user ID for audit log foreign key constraint
            let publicUserId = user?.id;
            if (user?.id) {
                const { data: userData } = await supabase
                    .from('users')
                    .select('id')
                    .eq('supabase_user_id', user.id)
                    .single();
                if (userData) publicUserId = userData.id;
            }

            // Create audit log
            await supabase.from('audit_logs').insert({
                id: crypto.randomUUID(),
                user_id: publicUserId,
                user_email: user?.email,
                organisation_id: currentOrg.id,
                resource_type: 'evidence',
                resource_id: emissionId,
                action: 'create',
                description: `Captured photo evidence`,
                new_data: JSON.stringify({
                    file_type: 'image/jpeg',
                    emission_entry_id: emissionId
                })
            });

            toast.success('Photo evidence uploaded successfully');
            if (onSuccess) onSuccess();
            return true;
        } catch (error) {
            console.error('Upload error:', error);
            toast.error(error.message || 'Failed to upload photo');
            return false;
        } finally {
            setEvidenceUploading(false);
        }
    };

    const handleEvidenceUpload = async (file, emissionId) => {
        if (!file || !currentOrg) return;

        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
        const allowedExts = ['pdf', 'jpg', 'jpeg', 'png', 'webp'];
        const fileExt = file.name.split('.').pop().toLowerCase();

        if (!allowedTypes.includes(file.type) && !allowedExts.includes(fileExt)) {
            toast.error('File must be PDF, JPG, PNG, or WebP');
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            toast.error('File must be less than 10MB');
            return;
        }

        setEvidenceUploading(true);
        try {
            const { data: { user: authUser } } = await supabase.auth.getUser();

            const fileExt = file.name.split('.').pop();
            const filePath = `${currentOrg.id}/${emissionId}/evidence_${Date.now()}.${fileExt}`;

            // Upload to Supabase Storage
            const { error: uploadError } = await supabase.storage
                .from('evidence-vault')
                .upload(filePath, file);

            if (uploadError) throw uploadError;

            // Create document record
            await supabase.from('documents').insert({
                id: crypto.randomUUID(),
                organisation_id: currentOrg.id,
                user_id: user?.id,
                emission_entry_id: emissionId,
                filename: file.name,
                file_type: file.type,
                file_size: file.size,
                storage_path: filePath,
                document_type: 'evidence'
            });

            // Get emission details for history record
            const { data: emissionData } = await supabase
                .from('emissions_data')
                .select('category, quantity, unit, co2_equivalent, entry_date')
                .eq('id', emissionId)
                .single();

            // Create entry history record for evidence attachment
            if (emissionData) {
                await supabase.from('entry_history').insert({
                    id: crypto.randomUUID(),
                    emission_entry_id: emissionId,
                    organisation_id: currentOrg.id,
                    user_id: user?.id,
                    action: 'evidence_added',
                    category: emissionData.category,
                    quantity: emissionData.quantity,
                    unit: emissionData.unit,
                    co2_equivalent: emissionData.co2_equivalent,
                    entry_date: emissionData.entry_date,
                    notes: `Attached evidence: ${file.name}`,
                    user_email: authUser?.email
                });
            }

            // Get public user ID for audit log foreign key constraint
            let publicUserId = user?.id;
            if (user?.id) {
                const { data: userData } = await supabase
                    .from('users')
                    .select('id')
                    .eq('supabase_user_id', user.id)
                    .single();
                if (userData) publicUserId = userData.id;
            }

            // Create audit log
            await supabase.from('audit_logs').insert({
                id: crypto.randomUUID(),
                user_id: publicUserId,
                user_email: authUser?.email,
                organisation_id: currentOrg.id,
                resource_type: 'evidence',
                resource_id: emissionId,
                action: 'create',
                description: `Uploaded evidence: ${file.name}`,
                new_data: JSON.stringify({
                    filename: file.name,
                    file_type: file.type,
                    emission_entry_id: emissionId
                })
            });

            toast.success('Evidence uploaded successfully!');
            if (onSuccess) onSuccess();
            return true;
        } catch (error) {
            console.error('Error uploading evidence:', error);
            toast.error(error.message || 'Failed to upload evidence');
            return false;
        } finally {
            setEvidenceUploading(false);
        }
    };

    const handleDeleteEvidence = async (entry) => {
        if (!entry.documents || entry.documents.length === 0) {
            toast.error('No evidence to delete');
            return;
        }

        setDeletingEvidence(true);
        try {
            const documents = entry.documents;

            // Delete files from storage
            for (const doc of documents) {
                if (doc.storage_path) {
                    await supabase.storage.from('evidence-vault').remove([doc.storage_path]);
                }
            }

            // Delete document records
            const docIds = documents.map(d => d.id);
            await supabase.from('documents').delete().in('id', docIds);

            // Log to entry history
            await supabase.from('entry_history').insert({
                id: crypto.randomUUID(),
                emission_entry_id: entry.id,
                organisation_id: currentOrg?.id,
                user_id: user?.id,
                action: 'evidence_removed',
                category: entry.category,
                quantity: entry.quantity,
                unit: entry.unit,
                co2_equivalent: entry.co2_equivalent,
                entry_date: entry.entry_date,
                notes: `Removed ${documents.length} evidence file(s)`,
                user_email: user?.email
            });

            // Get public user ID for audit log foreign key constraint
            let publicUserId = user?.id;
            if (user?.id) {
                const { data: userData } = await supabase
                    .from('users')
                    .select('id')
                    .eq('supabase_user_id', user.id)
                    .single();
                if (userData) publicUserId = userData.id;
            }

            // Create audit log
            await supabase.from('audit_logs').insert({
                id: crypto.randomUUID(),
                user_id: publicUserId,
                user_email: user?.email,
                organisation_id: currentOrg?.id,
                resource_type: 'evidence',
                resource_id: entry.id,
                action: 'delete',
                description: `Deleted ${documents.length} evidence file(s)`,
                old_data: JSON.stringify(documents.map(d => ({ filename: d.filename })))
            });

            toast.success('Evidence deleted successfully');
            if (onSuccess) onSuccess();
            return true;
        } catch (error) {
            console.error('Error deleting evidence:', error);
            toast.error(error.message || 'Failed to delete evidence');
            return false;
        } finally {
            setDeletingEvidence(false);
        }
    };

    return {
        evidenceUploading,
        deletingEvidence,
        uploadCapturedImage,
        handleEvidenceUpload,
        handleDeleteEvidence
    };
};
